/**
 * Tracking Service
 * UTM parameters, referrer info, and custom params
 */

import { createLogger } from '../core/logger';
import { UTMParams, ReferrerInfo } from '../types';

const logger = createLogger('Tracking');

// UTM parameter names
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

// Internal params to exclude from custom params
const INTERNAL_PARAMS = [
  'funnel', 'domain', 'v', 'inprogress', 'test', 'log', 'reset', 'embed',
  'preview', 'draft', 'demoMode', 'useLocal', 'useRemote'
];

/**
 * Extract UTM parameters from URL
 */
export function extractUTMParams(): UTMParams {
  const params = new URLSearchParams(window.location.search);
  const utm: UTMParams = {};

  for (const key of UTM_PARAMS) {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
    }
  }

  if (Object.keys(utm).length > 0) {
    logger.debug('UTM params extracted', utm);
  }

  return utm;
}

/**
 * Extract custom (non-UTM, non-internal) parameters
 */
export function extractCustomParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const custom: Record<string, string> = {};

  for (const [key, value] of params.entries()) {
    // Skip UTM params
    if (UTM_PARAMS.includes(key as typeof UTM_PARAMS[number])) continue;
    // Skip internal params
    if (INTERNAL_PARAMS.includes(key)) continue;
    // Add to custom
    custom[key] = value;
  }

  if (Object.keys(custom).length > 0) {
    logger.debug('Custom params extracted', custom);
  }

  return custom;
}

/**
 * Get referrer information
 */
export function getReferrerInfo(): ReferrerInfo {
  return {
    referrer: document.referrer || '',
    landingPage: window.location.href
  };
}

/**
 * Get all tracking data combined
 */
export function getAllTrackingData(): {
  utmParams: UTMParams;
  customParams: Record<string, string>;
  referrerInfo: ReferrerInfo;
} {
  return {
    utmParams: extractUTMParams(),
    customParams: extractCustomParams(),
    referrerInfo: getReferrerInfo()
  };
}

/**
 * Get Facebook browser cookie (_fbp)
 */
export function getFBP(): string | null {
  return getCookie('_fbp');
}

/**
 * Get Facebook click ID cookie (_fbc)
 */
export function getFBC(): string | null {
  return getCookie('_fbc');
}

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

/**
 * Device info (GDPR-compliant, no PII)
 */
export interface DeviceInfo {
  userAgent: string;
  platform: 'mobile' | 'tablet' | 'desktop';
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  language: string;
  timezone: string;
  touchEnabled: boolean;
}

/**
 * Detect device platform from user agent and screen size
 */
function detectPlatform(): 'mobile' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent.toLowerCase();
  const width = window.innerWidth;

  // Check for mobile keywords
  const isMobileUA = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
  const isTabletUA = /ipad|android(?!.*mobile)/i.test(ua);

  if (isTabletUA || (isMobileUA && width >= 768)) {
    return 'tablet';
  }
  if (isMobileUA || width < 768) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Get GDPR-compliant device information
 */
export function getDeviceInfo(): DeviceInfo {
  return {
    userAgent: navigator.userAgent,
    platform: detectPlatform(),
    screenWidth: screen.width,
    screenHeight: screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    language: navigator.language || 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    touchEnabled: 'ontouchstart' in window || navigator.maxTouchPoints > 0
  };
}

/**
 * Connection info (Network Information API)
 */
export interface ConnectionInfo {
  type: string | null;        // 4g, 3g, 2g, slow-2g, wifi, ethernet, none, unknown
  downlink: number | null;    // Mbps estimate
  rtt: number | null;         // Round-trip time in ms
  saveData: boolean;          // Data saver mode enabled
}

/**
 * Get connection/network information
 */
export function getConnectionInfo(): ConnectionInfo {
  const nav = navigator as Navigator & {
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
  };

  const conn = nav.connection;

  return {
    type: conn?.effectiveType || null,
    downlink: conn?.downlink || null,
    rtt: conn?.rtt || null,
    saveData: conn?.saveData || false
  };
}

/**
 * Browser capability flags
 */
export interface BrowserFlags {
  cookiesEnabled: boolean;
  doNotTrack: boolean;
  adBlockerDetected: boolean | null;  // null = not yet tested
}

/**
 * Get browser capability flags
 */
export function getBrowserFlags(): BrowserFlags {
  return {
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack === '1' || (window as Window & { doNotTrack?: string }).doNotTrack === '1',
    adBlockerDetected: null  // Will be tested async if needed
  };
}

/**
 * Engagement tracking state
 */
export interface EngagementData {
  tabSwitches: number;
  totalTimeMs: number;
  pageLoadedAt: number;
}

// Module-level engagement state
let engagementState: EngagementData = {
  tabSwitches: 0,
  totalTimeMs: 0,
  pageLoadedAt: Date.now()
};

let visibilityChangeHandler: (() => void) | null = null;

/**
 * Initialize engagement tracking (call once on page load)
 */
export function initEngagementTracking(): void {
  engagementState = {
    tabSwitches: 0,
    totalTimeMs: 0,
    pageLoadedAt: Date.now()
  };

  // Track tab switches via visibility change
  visibilityChangeHandler = () => {
    if (document.visibilityState === 'hidden') {
      engagementState.tabSwitches++;
      logger.debug('Tab switch detected', { count: engagementState.tabSwitches });
    }
  };

  document.addEventListener('visibilitychange', visibilityChangeHandler);
  logger.debug('Engagement tracking initialized');
}

/**
 * Get current engagement data
 */
export function getEngagementData(): EngagementData {
  return {
    ...engagementState,
    totalTimeMs: Date.now() - engagementState.pageLoadedAt
  };
}

/**
 * Clean up engagement tracking (call on page unload if needed)
 */
export function cleanupEngagementTracking(): void {
  if (visibilityChangeHandler) {
    document.removeEventListener('visibilitychange', visibilityChangeHandler);
    visibilityChangeHandler = null;
  }
}
